import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";

import { useGetOrganizations, getOrganizationsQueryKey } from "@/shared/api/generated-kubb/hooks/useGetOrganizations";
import { useGetOrganizationsId, getOrganizationsIdQueryKey } from "@/shared/api/generated-kubb/hooks/useGetOrganizationsId";
import { usePutOrganizationsId } from "@/shared/api/generated-kubb/hooks/usePutOrganizationsId";
import {
  organizationsApi,
  type CreateOrganizationRequest,
  type UpdateOrganizationRequest,
} from "@/api/organizations";
import { QUERY_KEYS } from "@/hooks/query-keys";
import { QUERY_SPECIFIC_CONFIG } from "@/shared/config";
import { handleError } from "@/shared/api/error-handler";

export function useOrganizations(options?: { enabled?: boolean }) {
  const query = useGetOrganizations({
    query: {
      enabled: options?.enabled ?? true,
      staleTime: QUERY_SPECIFIC_CONFIG.ORGANIZATION_LIST.staleTime,
      gcTime: QUERY_SPECIFIC_CONFIG.ORGANIZATION_LIST.gcTime,
      select: (data) => {
        // Извлекаем data из ResponseConfig и применяем селектор для уменьшения размера
        if (!data?.data) return null;
        // KAN-203/205: у GET /organizations в OpenAPI не описано тело ответа,
        // поэтому сген. тип — any. Описываем форму элемента явно.
        type OrganizationListItem = {
          id: string;
          name: string;
          avatar?: string | null;
          banner?: string | null;
          description?: string | null;
          memberCount?: number;
          stats?: unknown;
          subscriberCount?: number;
          isVerified?: boolean;
          url?: string | null;
          upcomingTournaments?: unknown;
        };
        return (data.data as OrganizationListItem[]).map((item) => ({
          id: item.id,
          name: item.name,
          avatar: item.avatar,
          banner: item.banner,
          description: item.description,
          memberCount: item.memberCount,
          stats: item.stats,
          subscriberCount: item.subscriberCount,
          isVerified: item.isVerified,
          url: item.url,
          upcomingTournaments: item.upcomingTournaments,
        }));
      },
    },
  });

  return {
    ...query,
    data: query.data ?? null,
  };
}

export function useInfiniteOrganizations(params?: {
  search?: string;
  filterBy?: string;
  sortBy?: string;
  sortDir?: string;
  limit?: number;
  enabled?: boolean;
}) {
  const normalizedParams = params ?? {};
  const { search, filterBy, sortBy, sortDir, limit: rawLimit } = normalizedParams;
  const queryKey = useMemo(
    () => QUERY_KEYS.organizationsPaged({ search, filterBy, sortBy, sortDir, limit: rawLimit }),
    [search, filterBy, sortBy, sortDir, rawLimit]
  );
  const limit = rawLimit ?? 24;

  return useInfiniteQuery({
    queryKey,
    enabled: normalizedParams.enabled ?? true,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const page = Number(pageParam) || 1;
      return organizationsApi.fetchPage({
        page,
        limit,
        ...(normalizedParams.search ? { search: normalizedParams.search } : {}),
        ...(normalizedParams.filterBy ? { filterBy: normalizedParams.filterBy } : {}),
        ...(normalizedParams.sortBy ? { sortBy: normalizedParams.sortBy } : {}),
        ...(normalizedParams.sortDir ? { sortDir: normalizedParams.sortDir } : {}),
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    staleTime: QUERY_SPECIFIC_CONFIG.ORGANIZATION_LIST.staleTime,
    gcTime: QUERY_SPECIFIC_CONFIG.ORGANIZATION_LIST.gcTime,
  });
}

export function useOrganization(id: string) {
  const query = useGetOrganizationsId(id, {
    query: {
      enabled: Boolean(id),
      staleTime: QUERY_SPECIFIC_CONFIG.ORGANIZATION_LIST.staleTime,
      gcTime: QUERY_SPECIFIC_CONFIG.ORGANIZATION_LIST.gcTime,
      select: (data) => {
        // Извлекаем data из ResponseConfig
        return data?.data ?? null;
      },
    },
  });

  return {
    ...query,
    data: query.data ?? null,
  };
}

export function useCreateOrganization() {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: CreateOrganizationRequest) =>
      organizationsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: getOrganizationsQueryKey() });
    },
    onError: (error: unknown) => {
      handleError(error, {
        component: "useCreateOrganization",
        action: "createOrganization",
      });
    },
  });

  return {
    ...mutation,
    mutate: (data: CreateOrganizationRequest) => mutation.mutate(data),
    mutateAsync: (data: CreateOrganizationRequest) =>
      mutation.mutateAsync(data),
  };
}

export function useUpdateOrganization(id: string) {
  const qc = useQueryClient();

  const mutation = usePutOrganizationsId({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getOrganizationsIdQueryKey(id) });
        qc.invalidateQueries({ queryKey: getOrganizationsQueryKey() });
      },
      onError: (error: unknown) => {
        handleError(error, {
          component: "useUpdateOrganization",
          action: "updateOrganization",
        });
      },
    },
  });

  // Обертка для совместимости: принимает только data, id уже привязан
  return {
    ...mutation,
    mutate: (data: UpdateOrganizationRequest) => {
      mutation.mutate({ id, data });
    },
    mutateAsync: (data: UpdateOrganizationRequest) => {
      return mutation.mutateAsync({ id, data });
    },
  };
}
